/* eslint-disable jira/naming/disallow-filenames */
// eslint-disable-next-line jira/filename-case
import React from 'react';
import {
	PeriscopeInsightsSkeleton,
	PeriscopeSuggestionsSkeleton,
	PeriscopeRelatedWorkSkeleton,
	DevelopmentPageWorkItemsVulnTabSkeleton,
	DevelopmentPageWorkItemsDeploymentTabSkeleton,
} from '@jira/platform__skeletons/src/ui/periscope-page/PeriscopeSuggestionsSkeleton.tsx';
import { Box } from '@atlaskit/primitives/compiled/box';
import { Flex } from '@atlaskit/primitives/compiled/flex';
import { cssMap } from '@atlaskit/css';
import { useIntl, defineMessage } from '@atlassian/jira-intl';
import { expVal } from '@atlassian/jira-feature-experiments';

const label = defineMessage({
	id: 'periscope.skeleton.label',
	defaultMessage: 'Loading',
	description: 'Label associated to the page skeleton while data is being requested.',
});

const insightsLabel = defineMessage({
	id: 'periscope.insights-skeleton.label',
	defaultMessage: 'Developer insights loading',
	description: 'Label associated to the page insights skeleton while data is being requested.',
});

const workItemsLabel = defineMessage({
	id: 'periscope.work-items-skeleton.work-items-label',
	defaultMessage: 'Work items loading',
	description: 'Label associated to the page work items skeleton while data is being requested.',
});

const workItemsVulnTabLabel = defineMessage({
	id: 'periscope.work-items-skeleton.work-items-vuln-tab-label',
	defaultMessage: 'Work items vulnerability tab loading',
	description:
		'Label associated to the page vulnerability tab work items skeleton while data is being requested.',
});

const workItemsDeploymentTabLabel = defineMessage({
	id: 'periscope.work-items-skeleton.work-items-deployment-tab-label',
	defaultMessage: 'Work items deployment tab loading',
	description:
		'Label associated to the page deployment tab work items skeleton while data is being requested.',
});

const styles = cssMap({
	mainContainer: {
		width: '100%',
		height: '100%',
		display: 'flex',
		boxSizing: 'border-box',
		overflow: 'hidden',
	},
	leftColumn: {
		flex: '2 1 0',
	},
	rightColumn: {
		flex: '1 1 0',
	},
	suggestionsContainer: {
		width: '100%',
		overflow: 'hidden',
		height: '650px',
		boxSizing: 'border-box',
	},
	insightsContainer: {
		overflow: 'hidden',
		width: '100%',
		height: '385px',
		boxSizing: 'border-box',
	},
	relatedWorkContainer: {
		width: '100%',
		height: '600px',
		boxSizing: 'border-box',
		overflow: 'hidden',
	},
	workItemsVulnTabContainer: {
		width: '100%',
		height: '420px',
		boxSizing: 'border-box',
		overflow: 'hidden',
	},
});

// Extend this for loading screen
export const Skeleton = (): React.JSX.Element => {
	const { formatMessage } = useIntl();
	if (expVal('jira_dev_periscope_whole_page_loading_state', 'isEnabled', false)) {
		return (
			<Flex
				xcss={styles.mainContainer}
				data-testId="software-periscope-periscope-page.ui.loading"
				aria-label={formatMessage(label)}
				direction="column"
			>
				<InsightsSkeleton />
				<RelatedWorkSkeleton />
			</Flex>
		);
	}

	return (
		<Flex
			xcss={styles.mainContainer}
			data-testId="software-periscope-periscope-page.ui.loading"
			aria-label={formatMessage(label)}
			gap="space.300"
		>
			<Flex xcss={styles.leftColumn} direction="column">
				<Box xcss={styles.insightsContainer}>
					<PeriscopeInsightsSkeleton />
				</Box>
				<Box xcss={styles.relatedWorkContainer}>
					<PeriscopeRelatedWorkSkeleton />
				</Box>
			</Flex>
			<Flex xcss={styles.rightColumn}>
				<Box xcss={styles.suggestionsContainer}>
					<PeriscopeSuggestionsSkeleton />
				</Box>
			</Flex>
		</Flex>
	);
};

export const InsightsSkeleton = (): React.JSX.Element => {
	const { formatMessage } = useIntl();
	return (
		<Box
			xcss={styles.insightsContainer}
			data-testId="software-periscope-periscope-page.insights-ui.loading"
			aria-label={formatMessage(insightsLabel)}
			paddingBlockEnd="space.400"
		>
			<PeriscopeInsightsSkeleton />
		</Box>
	);
};

// Extend this for loading screen
export const RelatedWorkSkeleton = (): React.JSX.Element => {
	const { formatMessage } = useIntl();
	return (
		<Box
			xcss={styles.relatedWorkContainer}
			aria-label={formatMessage(workItemsLabel)}
			paddingBlockEnd="space.400"
		>
			<PeriscopeRelatedWorkSkeleton />
		</Box>
	);
};

export const WorkItemsVulnTabSkeleton = (): React.JSX.Element => {
	const { formatMessage } = useIntl();
	return (
		<Box xcss={styles.workItemsVulnTabContainer} aria-label={formatMessage(workItemsVulnTabLabel)}>
			<DevelopmentPageWorkItemsVulnTabSkeleton />
		</Box>
	);
};

export const WorkItemsDeploymentTabSkeleton = (): React.JSX.Element => {
	const { formatMessage } = useIntl();
	return (
		<Box
			xcss={styles.relatedWorkContainer}
			aria-label={formatMessage(workItemsDeploymentTabLabel)}
			paddingBlockEnd="space.400"
		>
			<DevelopmentPageWorkItemsDeploymentTabSkeleton />
		</Box>
	);
};
